import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

export default function SupportTickets() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  const page = parseInt(searchParams.get('page') || '1');
  const statusFilter = searchParams.get('status') || 'all';
  const priorityFilter = searchParams.get('priority') || 'all';
  const categoryFilter = searchParams.get('category') || 'all';
  const assignedToFilter = searchParams.get('assigned_to') || 'all';
  const assignedToMe = searchParams.get('assigned_to_me') === 'true';
  
  const limit = 20;

  // Wrap fetchAgents in useCallback to avoid recreating it on every render
  const fetchAgents = useCallback(async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${backendUrl}/api/support/agents`);
      setAgents(res.data.agents || []);
    } catch (error) {
      console.error('Erreur chargement gestionnaires:', error);
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (assignedToMe) params.append('assigned_to_me', 'true');
      
      // Filtre par gestionnaire assigné
      if (assignedToFilter === 'unassigned') {
        // Backend devra gérer ce cas spécial
        params.append('unassigned', 'true');
      } else if (assignedToFilter !== 'all') {
        params.append('assigned_to', assignedToFilter);
      }

      const res = await axios.get(`${backendUrl}/api/support/tickets?${params}`);
      
      setTickets(res.data.tickets);
      setTotal(res.data.total);
      setTotalPages(res.data.pages);
    } catch (error) {
      console.error('Erreur chargement tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, priorityFilter, categoryFilter, assignedToFilter, assignedToMe, limit]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all' || value === false) {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    newParams.set('page', '1'); // Reset to page 1
    setSearchParams(newParams);
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: { variant: 'outline', text: 'Ouvert' },
      in_progress: { variant: 'default', text: 'En cours' },
      resolved: { variant: 'secondary', text: 'Résolu' },
      closed: { variant: 'secondary', text: 'Fermé' },
    };
    const config = variants[status] || variants.open;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: { variant: 'outline', text: 'Basse', className: '' },
      medium: { variant: 'secondary', text: 'Moyenne', className: '' },
      high: { variant: 'default', text: 'Haute', className: 'bg-orange-500/10 text-orange-700' },
      urgent: { variant: 'destructive', text: 'Urgente', className: '' },
    };
    const config = variants[priority] || variants.medium;
    return <Badge variant={config.variant} className={config.className}>{config.text}</Badge>;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      account_access: 'Accès compte',
      payment: 'Paiement',
      contract: 'Contrat',
      technical: 'Technique',
      other: 'Autre',
    };
    return labels[category] || category;
  };

  return (
    <div className="space-y-6 p-6" data-testid="support-tickets-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/support')}
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Tickets Support</h1>
            <p className="text-muted-foreground">{total} ticket{total > 1 ? 's' : ''} au total</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(v) => updateFilter('status', v)}>
              <SelectTrigger data-testid="status-filter">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="open">Ouverts</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="resolved">Résolus</SelectItem>
                <SelectItem value="closed">Fermés</SelectItem>
              </SelectContent>
            </Select>

            {/* Priority Filter */}
            <Select value={priorityFilter} onValueChange={(v) => updateFilter('priority', v)}>
              <SelectTrigger data-testid="priority-filter">
                <SelectValue placeholder="Toutes les priorités" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les priorités</SelectItem>
                <SelectItem value="low">Basse</SelectItem>
                <SelectItem value="medium">Moyenne</SelectItem>
                <SelectItem value="high">Haute</SelectItem>
                <SelectItem value="urgent">Urgente</SelectItem>
              </SelectContent>
            </Select>

            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={(v) => updateFilter('category', v)}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder="Toutes les catégories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les catégories</SelectItem>
                <SelectItem value="account_access">Accès compte</SelectItem>
                <SelectItem value="payment">Paiement</SelectItem>
                <SelectItem value="contract">Contrat</SelectItem>
                <SelectItem value="technical">Technique</SelectItem>
                <SelectItem value="other">Autre</SelectItem>
              </SelectContent>
            </Select>

            {/* Assigned To Filter (Gestionnaire) */}
            <Select value={assignedToFilter} onValueChange={(v) => updateFilter('assigned_to', v)}>
              <SelectTrigger data-testid="assigned-to-filter">
                <SelectValue placeholder="Tous les gestionnaires" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les gestionnaires</SelectItem>
                <SelectItem value="unassigned">Non assignés</SelectItem>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.full_name || agent.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Assigned to me */}
            <Button
              variant={assignedToMe ? 'default' : 'outline'}
              onClick={() => updateFilter('assigned_to_me', !assignedToMe)}
              data-testid="assigned-to-me-button"
            >
              Mes tickets
            </Button>

            {/* Reset */}
            {(statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all' || assignedToFilter !== 'all' || assignedToMe) && (
              <Button
                variant="ghost"
                onClick={() => {
                  setSearchParams({});
                }}
                data-testid="reset-filters-button"
              >
                Réinitialiser
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {loading ? 'Chargement...' : `${total} ticket${total > 1 ? 's' : ''}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Aucun ticket trouvé
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sujet</TableHead>
                    <TableHead>Utilisateur</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Priorité</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Assigné à</TableHead>
                    <TableHead>Créé le</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((ticket) => (
                    <TableRow key={ticket.id} data-testid={`ticket-row-${ticket.id}`}>
                      <TableCell className="font-medium">{ticket.subject}</TableCell>
                      <TableCell>{ticket.user_email}</TableCell>
                      <TableCell>{getCategoryLabel(ticket.category)}</TableCell>
                      <TableCell>{getPriorityBadge(ticket.priority)}</TableCell>
                      <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                      <TableCell>
                        {ticket.assigned_email ? (
                          <span className="text-sm">{ticket.assigned_email}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">Non assigné</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {ticket.created_at ? format(new Date(ticket.created_at), 'dd/MM/yyyy', { locale: fr }) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/support/tickets/${ticket.id}`)}
                          data-testid={`view-ticket-${ticket.id}`}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Voir
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-muted-foreground">
                    Page {page} sur {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => updateFilter('page', (page - 1).toString())}
                      data-testid="prev-page-button"
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Précédent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === totalPages}
                      onClick={() => updateFilter('page', (page + 1).toString())}
                      data-testid="next-page-button"
                    >
                      Suivant
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
